import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import styled from "styled-components/macro";

import API from "Signups/API";
import { useIsSignupClosed } from "Signups/SharedComponents/useIsSignupClosed";
import { useNewSignupCreationFlow } from "Signups/SignupView/NewSignup";
import { useSignups } from "Signups/state";
import { useIsSignupEditMode } from "Signups/stateHooks";
import classNames from "classnames";
import { AnimatePresence, motion } from "framer-motion";

import FnrDragNDrop from "UI/FnrDragNDrop";
import FnrIcon from "UI/FnrIcon";
import {
  HoverChild,
  HoverParent,
  HoverSelectedBackground,
} from "UI/HoverChildren";
import { colors } from "UI/colors";

import useComponentIsMounted from "Utilities/Hooks/useComponentIsMounted";

import SignupItem from "./SignupItem";
import CustomizeButton from "./CustomizeButton";

export default function ItemsList({ items }) {
  const [
    {
      currentSignupList: { ID: signupListID },
    },
    dispatch,
  ] = useSignups();
  const { signupItemID } = useParams();

  const { isArchived } = useIsSignupClosed();
  const { isSignupEditMode: showItemList } = useIsSignupEditMode();

  const [savingOrder, setSavingOrder] = useState(false);

  const saveNewOrder = ({ orderedItems }) => {
    let orderNumber = 0;
    orderedItems.forEach((item) => {
      item.orderNumber = orderNumber;
      orderNumber++;
    });

    dispatch({ type: "Reorder Signuplist Items", value: orderedItems });

    const orderedIDs = orderedItems.map((item) => item.ID);
    setSavingOrder(true);
    API.reorderItems(signupListID, {
      signupListID,
      signupListItemIDs: orderedIDs,
    })
      .post()
      .then(() => {
        // nothing needed here, since new order has already been dispatched
      })
      .catch(() => {
        alert(
          "Oh no! Something went wrong while re-ordering the Signup Items. Please refresh and try again."
        );
      })
      .finally(() => setSavingOrder(false));
  };

  const { hideItemsList } = useNewSignupCreationFlow();

  const isMounted = useComponentIsMounted();

  const hidden = { opacity: 0, height: 0 };
  const visible = { opacity: 1, height: "auto" };

  return (
    <Wrapper>
      <Inside>
        <List>
          <HoverParent CustomSelectedElement={CustomSelectedElement}>
            {showItemList && (
              <AnimatePresence>
                {!hideItemsList && (
                  <CustomizeButton
                    isMounted={isMounted}
                    signupItemID={signupItemID}
                    signupListID={signupListID}s
                  />
                )}
              </AnimatePresence>
            )}
            <ItemsListContainer>
              <FnrDragNDrop
                items={items}
                itemIdName="ID"
                ItemComponent={SignupItem}
                onRelease={saveNewOrder}
                disabled={savingOrder}
                itemHasDragTrigger={true}
                noMargin
              />
            </ItemsListContainer>
            {showItemList && (
              <AnimatePresence>
                {!(hideItemsList || isArchived) && (
                  <AddNewItemContainer
                    initial={isMounted ? hidden : null}
                    animate={visible}
                  >
                    <AddNewItemContainerInside>
                      <HoverChild
                        selected={
                          signupItemID === "new" || signupItemID === "another"
                        }
                      >
                        <AddNewItem
                          className={classNames({
                            selected:
                              signupItemID === "new" ||
                              signupItemID === "another",
                          })}
                        >
                          <Background />
                          <Link to={`/signups/${signupListID}/items/new/info`}>
                            <AddNewItemInside
                              whileHover={{ color: colors.fnBlue }}
                              whileTap={{ scale: 0.94 }}
                            >
                              <FnrIcon far fa-plus-square />
                              <AddNewItemText>Add New Item</AddNewItemText>
                            </AddNewItemInside>
                          </Link>
                        </AddNewItem>
                      </HoverChild>
                    </AddNewItemContainerInside>
                  </AddNewItemContainer>
                )}
              </AnimatePresence>
            )}
          </HoverParent>
        </List>
      </Inside>
    </Wrapper>
  );
}

const CustomSelectedElement = styled(HoverSelectedBackground)`
  opacity: 0 !important;
`;

const Wrapper = styled(motion.div)`
  width: 320px;
  position: relative;
  z-index: 20;
  flex-shrink: 0;
  height: 100vh;
  @media (max-width: 1060px) {
    height: auto;
  }
`;

const Inside = styled.div`
  width: 308px;
  box-sizing: border-box;
  position: absolute;
  right: 0;
  top: 12px;
  bottom: 98px;

  @media (max-width: 1060px) {
    position: static;
    padding-top: 12px;
    padding-left: 12px;
    max-height: 271px; // max at showing 3 items, otherwise other tabbed content has no room. Not ideal, should improve later.
    overflow-y: auto;
  }
`;

const List = styled.div`
  border-radius: 12px;
  background: #fff;
  width: 100%;
  overflow: auto;
  box-shadow: rgba(0, 0, 0, 0.1) 0 1px 4px;
  opacity: 1;
  max-height: calc(100vh - 200px);
`;

const ItemsListContainer = styled.div`
  padding: 6px;
`;

const AddNewItemContainer = styled(motion.div)`
  overflow: hidden;
`;
const AddNewItemContainerInside = styled.div`
  padding: 6px 6px;
  border-top: 1px solid #eceff0;
`;

const AddNewItem = styled.div`
  & a {
    text-decoration: none !important;
    color: inherit;
  }

  user-select: none;
  position: relative;
  z-index: 100;
`;
const AddNewItemInside = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 400;
  font-size: 15px;
  padding-top: 6px;
  padding-bottom: 6px;
  position: relative;
  z-index: 200;
`;
const AddNewItemText = styled.div`
  margin-left: 6px;
`;

const Background = styled.div`
  border-radius: 8px;
  position: absolute;
  opacity: 0;
  z-index: 1;
  background: #eceff0;
  transition: transform 0.2s, opacity 0.2s, inset 0.2s;
  inset: 5px 6px;

   ${AddNewItem}:hover & {
    inset: 2px;
  }

   ${AddNewItem}.selected & {
    opacity: 1;
    transform: none;
    inset: 0;
  }
`;
